import { useTranslation } from 'react-i18next'; // Hook for internationalization
import {useDispatch, useSelector} from 'react-redux'; // Hook for accessing Redux state
import React, {useLayoutEffect, useState} from 'react'; // React hooks for lifecycle and state
import { permissionObj } from '../../../../Helpers/permission';
import {useLocation, useNavigate} from "react-router-dom";
import routes from "../../../../Routes/routes";
import { TABS_VISITOR} from "../Model/Model";
import {VISITOR_SUB_VIEW_MODEL} from "../Invitation/Model/Model";
import {MODELS} from "../../../../Apis/Models";
import {TABLES} from "../../../../Apis/Tables";
import {DeleteItemsApi} from "../../../../reduxToolkit/Commons/CommonsApi";
import { VISITOR_VIEWS_FROM } from '../Guard/Model/Model';
import {errorMsg} from "../../../../Apis/functions";

/**
 * @Email : kpozos@ibl.mx
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer
 *
 * VisitorApp manages visitor data and associated user interactions.
 * It handles permissions, step navigation, modal visibility, and routing.
 */

export const VisitorMainApp = () => {
    // Initialize translation function from i18next
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // Hook to navigate programmatically between routes
    const navigate = useNavigate();
    const location = useLocation();
    let {state} = location;
    const currentTab = state?.currentTab;
    const isTabRequestedByNavigation = currentTab !== null && typeof currentTab !== 'undefined';
    const currentSubView = state?.currentSubView;
    const userId = useSelector((state) => state.authenticatioauthennSlice.getUserByIdAtEntry.userId);
    // State to store the available steps based on permissions
    const [steps, setSteps] = useState([]);
    // State to track the currently active step index
    const [activeStep, setActiveStep] = useState(0);
    // State to track selected items in the current step
    const [selectedList, setSelectedList] = useState([]);
    // State to control visibility of the delete confirmation modal
    const [deleteModal, setDeleteModal] = useState(false)
    const [subView, setSubView] = useState(null);
    const [search, setSearch] = useState(false); // Search flag
    const [bodyForExport, setBodyForExport] = useState(''); // Data to export
    const [searchElement, setSearchElement] = useState(''); // Search filter value
    const [criteria, setCriteria] = useState();
    const [finalArray, setFinalArray] = useState([]); // Final array to store filtered data
    const [filterDialogShow, setFilterDialogShow] = useState(false);
    const [modified, setModified] = useState(false);
    const [moduleId, setModuleId] = useState(`${MODELS.VISITOR_INVITATION}`);
    const [option, setOption] = useState(`${TABLES.USER_INVITATION}`);
    const [showVisitorModal, setShowVisitorModal] = useState(false);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false); // Simple search flag
    // Retrieve development mode flag from Redux store
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    // Retrieve user permissions from Redux store
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    // Flag to indicate if the current user is a visitor
    const [viewOrigin, setViewOrigin] = useState(VISITOR_VIEWS_FROM.VISITOR_MANAGEMENT);
    /**
     * Effect hook to set up steps based on permissions and development mode.
     * Also sets the default active step depending on the available permissions.
     */
    useLayoutEffect(() => {
        let activeTab = null;
        const stepConfig = [
            { tab: TABS_VISITOR.INVITATIONS.TRANS, perm: [permissionObj?.WEB_VISITOR_INVITATION_READ], index:TABS_VISITOR.INVITATIONS.INDEX },//Change to invitation
            { tab: TABS_VISITOR.REQUESTS.TRANS, perm: [permissionObj?.WEB_VISITOR_REQUEST_READ], index:TABS_VISITOR.REQUESTS.INDEX },
            { tab: TABS_VISITOR.VISITORS.TRANS, perm: [permissionObj?.WEB_VISITOR_READ, permissionObj?.WEB_REQUEST_ACCESS_READ], index:TABS_VISITOR.VISITORS.INDEX },
            { tab: TABS_VISITOR.TYPES.TRANS, perm: [permissionObj?.WEB_VISITOR_TYPE_READ], index:TABS_VISITOR.TYPES.INDEX },
        ];

        const auxStep = stepConfig.reduce((acc, { tab, perm, index }) => {
            const hasPermission = DEV || perm.some(p => permission?.includes(p));
            if (hasPermission) {
                acc.push({label:t(tab), index:index});
                if(activeTab === null) activeTab = index;
            }
            return acc;
        }, []);

        setSteps(auxStep);

        // The tab asked for by the navigation state is a view of its own: without permission for it
        // the reader goes to the forbidden page instead of landing on a tab that is not in the list.
        if (auxStep.length > 0 && isTabRequestedByNavigation && !auxStep.some(({index}) => index === currentTab)) {
            navigate(routes.forbidden);
            return;
        }

        if(activeTab !== null && !isTabRequestedByNavigation) handleStepChange(activeTab);
    }, [t, permission]);

    useLayoutEffect(() => {
        if (!isTabRequestedByNavigation || steps.length === 0) return;
        // Same rule as the tab initialization: a tab the role cannot see is a forbidden view.
        if (!steps.some(({index}) => index === currentTab)) {
            navigate(routes.forbidden);
            return;
        }
        handleStepChange(currentTab);
    }, [location, steps]);

    /**
     * Handles changing the active step.
     * Resets the selected items list when the step changes.
     *
     * @param {number} step - The index of the step to activate.
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
        setSelectedList([]);
        setFinalArray([]);
        setSearchElement('');
        setSearch(false);
        navigate(location.pathname, { replace: true, state: {} });

        subViewChange(step);
        switch (step) {
            case TABS_VISITOR.INVITATIONS.INDEX:
                setModuleId(MODELS.VISITOR_INVITATION);
                setOption(TABLES.USER_INVITATION);
                break;
            case TABS_VISITOR.REQUESTS.INDEX:
                setModuleId(MODELS.VISITOR_REQUEST);
                setOption(TABLES.VISITOR_REQUEST);
                break;
            case TABS_VISITOR.VISITORS.INDEX:
                setModuleId(MODELS.VISITOR);
                setOption(TABLES.VISITOR);
                break;
            case TABS_VISITOR.TYPES.INDEX:
                setModuleId(MODELS.VISITOR_TYPE);
                setOption(TABLES.VISITOR_TYPES);
                break;
        }
    };

    /**
     * Handles the "create" action depending on the current active step.
     * Navigates to the visitor type creation route or shows the invitation modal.
     */
    const subViewChange = (step) => {
        if(currentSubView){
            setSubView(currentSubView);
            return;
        }

        switch (step) {
            case TABS_VISITOR.INVITATIONS.INDEX:
                setSubView(VISITOR_SUB_VIEW_MODEL.INVITATION.ONGOING);
                break;
            case TABS_VISITOR.REQUESTS.INDEX:
                setSubView(VISITOR_SUB_VIEW_MODEL.REQUESTS.REQUESTED_INVITATIONS);
                break;
            case TABS_VISITOR.VISITORS.INDEX:
                const activeRequest = DEV || permission?.includes(permissionObj.WEB_REQUEST_ACCESS_READ);
                const visitors =  DEV || permission?.includes(permissionObj.WEB_VISITOR_READ);
                const subView = (activeRequest && visitors)
                    ? VISITOR_SUB_VIEW_MODEL.VISITORS.VISITORS
                    : activeRequest
                        ? VISITOR_SUB_VIEW_MODEL.VISITORS.ACTIVE_REQUESTS
                        : VISITOR_SUB_VIEW_MODEL.VISITORS.VISITORS;
                setSubView(subView);
                break;
            case TABS_VISITOR.TYPES.INDEX:
                break;
        }
    };

    /**
     * Handles the "create" action depending on the current active step.
     * Navigates to the visitor type creation route or shows the invitation modal.
     */
    const goCreate = () => {
        switch (activeStep) {
            case TABS_VISITOR.INVITATIONS.INDEX:
                navigate(routes.visitorInvitation.create);
                break;
            case TABS_VISITOR.REQUESTS.INDEX:
                navigate(routes.visitorRequest.main)
                break;
            case TABS_VISITOR.VISITORS.INDEX:
                setShowVisitorModal(true);
                break;
            case TABS_VISITOR.TYPES.INDEX:
                navigate(routes.visitorType.create, { state: { activeStep: activeStep } });
                break;
        }
    };

    const permissionButtons = (list) => {
        const permissionRequired = list[activeStep];

        if (!permissionRequired) {
            return false;
        }
        return DEV || permission?.includes(permissionRequired);
    };

    const deleteSelected = (ids) => {
        const tableName = option;
        if(tableName === null) return;

        const body = ids;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(({ payload: { data:{ success, code }}}) => {
                if(success){
                    setModified(true);
                    setSelectedList([]);
                }else errorMsg(code);
            });
        }
    };


    // Return all necessary state and handlers for UI consumption.
    // Separates logic from UI rendering for better reuse and testing.
    return {
        t,
        DEV,
        dispatch,
        userId,
        permission,
        navigate,
        steps,
        activeStep,
        setActiveStep,
        setDeleteModal,
        deleteModal,
        selectedList,
        setSelectedList,
        subView,
        setSubView,
        search,
        setSearch,
        moduleId,
        criteria,
        setCriteria,
        option,
        bodyForExport,
        setBodyForExport,
        searchElement,
        setSearchElement,
        finalArray,
        setFinalArray,
        filterDialogShow,
        setFilterDialogShow,
        modified,
        setModified,
        showVisitorModal,
        setShowVisitorModal,
        handleStepChange,
        goCreate,
        permissionButtons,
        deleteSelected,
        setOption,
        setModuleId,
        viewOrigin,
        setViewOrigin,
        isAllChecked,
        setIsAllChecked,
        simpleSearch,
        setSimpleSearch
    };
};
